import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Flex, Dropdown, Icon } from 'ui';
import Sticky from '../../../common/sticky/sticky';
// eslint-disable-next-line
import ServiceCenterToggle from '../../../header/service-center-toggle/containers/service-center-toggle-container';
import { fetchPlants, fetchSkills } from '../../containers/skills/skills-actions';

const Toolbar = styled(Flex)`
  height: 55px;
  background-color: rgb(245, 248, 250) !important;
  margin: 0 -20px;
  padding-left: 20px;
`;

export const StyledInfoIcon = styled(Icon)`
  margin-left: 5px;
  margin-top: 5px;
  vertical-align: middle;
`;

const ToolbarItem = styled.div`
  padding: 0 15px;
  ${p => !p.noBorder && `border-left: 1px solid ${p.theme.colors.darkThemeSecondary}`}
`;

const StyledSpan = styled.span`
  font-size: 13px;
  line-height: 2.5;
  margin-right: 5px;
`;

const StyledDropdown = styled(Dropdown)`
  width: 200px;
`;

const SkillsToolbar = ({
  intl,
}) => {
  const dispatch = useDispatch();
  const menuVisible = useSelector(state => state.header.menuVisible);
  const serviceCenterId = useSelector(state => state.serviceCenter.activeServiceCenter);
  const plants = useSelector(state => state.skills.plants);
  const {
    isLoadingPlants,
    isLoadingSkills,
    isLoadingSkillsNext,
  } = useSelector(state => state.skills);
  const [dropdownInfo, setDropdownInfo] = useState({ plants: [], regions: [], technicians: [] });
  const [filterValues, setFilterValues] = useState({
    plant: null,
    region: null,
    technician: null,
  });
  const serviceCenterToggleStyle = `
    margin: 0 10px;
    width: 200px;
  `;

  useEffect(() => {
    if (serviceCenterId && !isLoadingPlants) {
      dispatch(fetchPlants());
    }
  }, [serviceCenterId]);

  useDeepCompareEffect(() => {
    if (filterValues.plant?.id || filterValues.region?.id || filterValues.technician?.id) {
      const values = {
        plantId: filterValues.plant?.id,
        regionId: filterValues.region?.id,
        technicianId: filterValues.technician?.id,
      };
      dispatch(fetchSkills(values));
    }
  }, [filterValues]);

  const fillDropdowns = () => {
    setDropdownInfo(s => ({
      ...s,
      plants,
    }));
  };

  useDeepCompareEffect(() => {
    fillDropdowns();
  }, [plants]);

  const handlePlantChange = (v) => {
    const plant = plants?.find(p => p.id === v);
    setFilterValues(() => ({
      plant,
      region: null,
      technician: null,
    }));
    setDropdownInfo(s => ({
      ...s,
      regions: plant?.items || [],
    }));
  };

  const handleRegionChange = (v) => {
    const region = filterValues.plant?.items?.find(r => r.id === v);
    setFilterValues(s => ({
      ...s,
      region,
      technician: null,
    }));
    setDropdownInfo(s => ({
      ...s,
      technicians: region?.items || [],
    }));
  };

  const handleTechnicianChange = (v) => {
    const technician = filterValues.region?.items?.find(t => t.id === v);
    setFilterValues(s => ({
      ...s,
      technician,
    }));
  };

  return (
    <>
      <Sticky
        menuVisible={menuVisible}
        translateYEnd='-106px'
      >
        <Toolbar aic jcfs ffrn>
          <ToolbarItem noBorder={true}>
            <StyledSpan>
              {intl.formatMessage({ id: 'holidays.CUSTOMER_CENTER' })}{':'}
            </StyledSpan>
            <ServiceCenterToggle
              needsRedirect={false}
              extraStyles={serviceCenterToggleStyle}
              onlyAdmin={true}
            />
          </ToolbarItem>
          <ToolbarItem>
            <StyledSpan>
              {intl.formatMessage({ id: 'holidays.PLANT' })}{':'}
            </StyledSpan>
            <StyledDropdown
              placeholder={intl.formatMessage({ id: 'order.groupDropdown.DROPDOWN_DEFAULT' })}
              onChange={handlePlantChange}
              values={dropdownInfo?.plants || []}
              disabled={
                !dropdownInfo?.plants?.length ||
                isLoadingPlants ||
                isLoadingSkills ||
                isLoadingSkillsNext
              }
              valueLabel='name'
              valueKey='id'
              value={filterValues.plant?.id}
              dropdownMatchSelectWidth={false}
            />
          </ToolbarItem>
          <ToolbarItem>
            <StyledSpan>
              {intl.formatMessage({ id: 'holidays.REGION' })}{':'}
            </StyledSpan>
            <StyledDropdown
              placeholder={intl.formatMessage({ id: 'order.groupDropdown.DROPDOWN_DEFAULT' })}
              onChange={handleRegionChange}
              values={dropdownInfo?.regions || []}
              disabled={
                !dropdownInfo?.regions?.length ||
                !filterValues.plant?.id ||
                isLoadingPlants ||
                isLoadingSkills ||
                isLoadingSkillsNext
              }
              valueLabel='name'
              valueKey='id'
              value={filterValues.region?.id}
              dropdownMatchSelectWidth={false}
            />
          </ToolbarItem>
          <ToolbarItem>
            <StyledSpan>
              {intl.formatMessage({ id: 'holidays.FSE' })}{':'}
            </StyledSpan>
            <StyledDropdown
              placeholder={intl.formatMessage({ id: 'order.groupDropdown.DROPDOWN_DEFAULT' })}
              onChange={handleTechnicianChange}
              values={dropdownInfo?.technicians || []}
              disabled={
                !dropdownInfo?.technicians?.length ||
                !filterValues.plant?.id ||
                !filterValues.region?.id ||
                isLoadingPlants ||
                isLoadingSkills ||
                isLoadingSkillsNext
              }
              valueLabel='name'
              valueKey='id'
              value={filterValues.technician?.id}
              dropdownMatchSelectWidth={false}
            />
          </ToolbarItem>
        </Toolbar>
      </Sticky>
    </>
  );
};
SkillsToolbar.propTypes = {
  intl: PropTypes.object,
};

export default injectIntl(SkillsToolbar);
